// src/components/Verfuegbarkeiten.js

import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import {
  collection,
  addDoc,
  onSnapshot,
  deleteDoc,
  doc,
  query,
  where,
  updateDoc
} from 'firebase/firestore';
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead
} from '@mui/material';

function Verfuegbarkeiten({ tenantId, selectedEmployee, showSnackbar }) {
  const [weekdays, setWeekdays] = useState({
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false,
    Sunday: false,
  });
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('16:00');
  const [endDate, setEndDate] = useState('');
  const [employeeAvailabilities, setEmployeeAvailabilities] = useState([]);
  const [editingAvailability, setEditingAvailability] = useState(null);

  // Verfügbarkeiten laden, wenn Mitarbeiter gewechselt wird
  useEffect(() => {
    if (!tenantId || !selectedEmployee) {
      setEmployeeAvailabilities([]);
      return;
    }
    const qAvail = query(
      collection(db, 'tenants', tenantId, 'availability'),
      where('employeeId', '==', selectedEmployee)
    );
    const unsubAvail = onSnapshot(qAvail, (snapshot) => {
      const data = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setEmployeeAvailabilities(data);
    });
    return () => unsubAvail();
  }, [tenantId, selectedEmployee]);

  // Validierung Verfügbarkeit
  const validateAvailabilityInputs = () => {
    const selectedDays = Object.keys(weekdays).filter((day) => weekdays[day]);
    if (!selectedEmployee || selectedDays.length === 0 || !startTime || !endTime || !endDate) {
      showSnackbar('Bitte alle Felder für Verfügbarkeit ausfüllen!');
      return false;
    }
    if (new Date(`${endDate}T${endTime}`) <= new Date(`${endDate}T${startTime}`)) {
      showSnackbar('Endzeit muss nach der Startzeit liegen!');
      return false;
    }
    return true;
  };

  // (A) Neue Verfügbarkeit anlegen
  const handleCreateRecurringAvailability = async () => {
    if (!validateAvailabilityInputs()) return;

    try {
      const selectedDays = Object.keys(weekdays).filter((day) => weekdays[day]);
      await addDoc(collection(db, 'tenants', tenantId, 'availability'), {
        employeeId: selectedEmployee,
        type: 'recurring',
        weekdays: selectedDays,
        startTime,
        endTime,
        endDate,
      });
      showSnackbar('Wiederkehrende Verfügbarkeit hinzugefügt!');
      resetAvailabilityForm();
    } catch (error) {
      console.error('Fehler beim Hinzufügen der Verfügbarkeit:', error);
      showSnackbar('Fehler beim Hinzufügen der Verfügbarkeit.');
    }
  };

  // (B) Bestehende Verfügbarkeit aktualisieren
  const handleUpdateAvailability = async () => {
    if (!editingAvailability || !validateAvailabilityInputs()) return;

    try {
      const selectedDays = Object.keys(weekdays).filter((day) => weekdays[day]);
      const availabilityRef = doc(
        db,
        'tenants',
        tenantId,
        'availability',
        editingAvailability.id
      );
      await updateDoc(availabilityRef, {
        weekdays: selectedDays,
        startTime,
        endTime,
        endDate,
      });
      showSnackbar('Verfügbarkeit aktualisiert!');
      setEditingAvailability(null);
      resetAvailabilityForm();
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Verfügbarkeit:', error);
      showSnackbar('Fehler beim Aktualisieren der Verfügbarkeit.');
    }
  };

  // (C) Verfügbarkeit löschen
  const handleDeleteAvailability = async (id) => {
    try {
      const availabilityRef = doc(db, 'tenants', tenantId, 'availability', id);
      await deleteDoc(availabilityRef);
      showSnackbar('Verfügbarkeit gelöscht!');
      if (editingAvailability && editingAvailability.id === id) {
        setEditingAvailability(null);
        resetAvailabilityForm();
      }
    } catch (error) {
      console.error('Fehler beim Löschen der Verfügbarkeit:', error);
      showSnackbar('Fehler beim Löschen der Verfügbarkeit.');
    }
  };

  // Reset-Funktionen
  const resetAvailabilityForm = () => {
    setWeekdays({
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
      Sunday: false,
    });
    setStartTime('08:00');
    setEndTime('16:00');
    setEndDate('');
  };

  // Checkbox-Handler
  const handleWeekdayChange = (event) => {
    setWeekdays({
      ...weekdays,
      [event.target.name]: event.target.checked,
    });
  };

  // Formulardaten in State laden (beim Bearbeiten)
  const startEditingAvailability = (availability) => {
    setEditingAvailability(availability);
    setWeekdays({
      Monday: availability.weekdays.includes('Monday'),
      Tuesday: availability.weekdays.includes('Tuesday'),
      Wednesday: availability.weekdays.includes('Wednesday'),
      Thursday: availability.weekdays.includes('Thursday'),
      Friday: availability.weekdays.includes('Friday'),
      Saturday: availability.weekdays.includes('Saturday'),
      Sunday: availability.weekdays.includes('Sunday'),
    });
    setStartTime(availability.startTime);
    setEndTime(availability.endTime);
    setEndDate(availability.endDate);
  };

  const cancelEditingAvailability = () => {
    setEditingAvailability(null);
    resetAvailabilityForm();
  };

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Verfügbarkeiten verwalten
      </Typography>

      {/* Liste vorhandener Verfügbarkeiten */}
      {employeeAvailabilities.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Vorhandene Verfügbarkeiten
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Wochentage</TableCell>
                <TableCell>Startzeit</TableCell>
                <TableCell>Endzeit</TableCell>
                <TableCell>Enddatum</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employeeAvailabilities.map((avail) => (
                <TableRow key={avail.id}>
                  <TableCell>{avail.weekdays.join(', ')}</TableCell>
                  <TableCell>{avail.startTime}</TableCell>
                  <TableCell>{avail.endTime}</TableCell>
                  <TableCell>{avail.endDate}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      sx={{ mr: 1 }}
                      onClick={() => startEditingAvailability(avail)}
                    >
                      Bearbeiten
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDeleteAvailability(avail.id)}
                    >
                      Löschen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Formular für neue/bearbeitete Verfügbarkeit */}
      <Box sx={{ mt: 2 }}>
        <FormGroup row>
          {Object.keys(weekdays).map((day) => (
            <FormControlLabel
              key={day}
              control={
                <Checkbox
                  checked={weekdays[day]}
                  onChange={handleWeekdayChange}
                  name={day}
                />
              }
              label={day}
            />
          ))}
        </FormGroup>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Enddatum"
              type="date"
              variant="outlined"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              label="Startzeit"
              type="time"
              variant="outlined"
              fullWidth
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              label="Endzeit"
              type="time"
              variant="outlined"
              fullWidth
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          {editingAvailability ? (
            <>
              <Button
                variant="contained"
                color="primary"
                sx={{ mr: 2 }}
                onClick={handleUpdateAvailability}
              >
                Verfügbarkeit aktualisieren
              </Button>
              <Button variant="outlined" onClick={cancelEditingAvailability}>
                Abbrechen
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleCreateRecurringAvailability}>
              Neue Verfügbarkeit hinzufügen
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export default Verfuegbarkeiten;
